import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { formatDate } from '@/lib/utils/date';
import { useDialerwiseUsage } from '../api';
import type { DialerwiseUsageRow } from '../types';

/**
 * SADMIN report — cross-dialer call-duration rollup for a date range.
 * Ports the dashboard "Dialer Usage" card from legacy obd_frontend.
 *
 * Note: at backend port-time the Java DAO is dead code (SQL commented
 * out), so the endpoint returns an empty array until that is restored.
 * The page is still useful as the front-end is ready for live data.
 */
export function DialerUsageRollupPage() {
  const userId = Number(useAuthStore((s) => s.userId));
  const usage = useDialerwiseUsage();
  const rows = useMemo<DialerwiseUsageRow[]>(
    () => usage.data ?? [],
    [usage.data],
  );

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      toast.error('Pick both a start and end date.');
      return;
    }
    if (startDate > endDate) {
      toast.error('Start date cannot be after end date.');
      return;
    }
    usage.mutate({
      userId,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
    });
  };

  const totalSeconds = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.totalDuration) || 0), 0),
    [rows],
  );

  const columns = useMemo<ColumnDef<DialerwiseUsageRow>[]>(
    () => [
      {
        accessorKey: 'dialerName',
        header: 'Dialer',
        cell: ({ row }) => (
          <span className="font-medium">{row.original.dialerName}</span>
        ),
      },
      {
        accessorKey: 'cli',
        header: 'CLI',
        cell: ({ row }) => (
          <span className="font-mono text-sm">{row.original.cli}</span>
        ),
      },
      {
        accessorKey: 'totalDuration',
        header: 'Total duration',
        size: 180,
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">
            {formatSeconds(row.original.totalDuration)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="Dialer Usage Rollup"
        description="Cross-dialer call-duration totals for a date range. Each row aggregates by dialer and CLI."
      />

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
          <CardDescription>
            Pick a date range and search. Results sum call duration across
            every campaign that touched each dialer.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSearch}
            className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_1fr_auto]"
          >
            <div className="grid gap-1.5">
              <Label htmlFor="startDate">
                Start date<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <DatePicker
                value={startDate ?? null}
                onChange={(d) => setStartDate(d ?? undefined)}
                toDate={yesterday}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="endDate">
                End date<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <DatePicker
                value={endDate ?? null}
                onChange={(d) => setEndDate(d ?? undefined)}
                toDate={yesterday}
              />
            </div>
            <Button type="submit" disabled={usage.isPending}>
              {usage.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {rows.length > 0 && (
        <div className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{rows.length}</span>{' '}
          row{rows.length === 1 ? '' : 's'} ·{' '}
          <span className="font-medium text-foreground">
            {formatSeconds(totalSeconds)}
          </span>{' '}
          total
        </div>
      )}

      <DataTable<DialerwiseUsageRow, unknown>
        tableId="dialers:usage-rollup:v1"
        columns={columns}
        data={rows}
        isLoading={usage.isPending}
        searchPlaceholder="Filter by dialer or CLI…"
        emptyTitle={usage.isSuccess ? 'No usage records' : 'No results yet'}
        emptyDescription={
          usage.isSuccess
            ? 'No call traffic recorded for any dialer in the selected range.'
            : 'Pick a date range and click Search to load usage data.'
        }
        getRowId={(row, idx) => `${row.dialerName}-${row.cli}-${idx}`}
        exportFilename="dialer-usage-rollup.csv"
        pageSize={25}
      />
    </FillPage>
  );
}

/** Format integer seconds as `Hh Mm Ss` — drop leading zero parts. */
function formatSeconds(input: number | string): string {
  const total = Math.max(0, Math.floor(Number(input) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
